import Icon from '@/components/icon/Icon';
import text from '@/text';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Form, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import { useStaffPriceEditContext } from '../StaffPriceEditContext';

type EditablePriceFieldProps = {
  fieldKey: string;
  value: number | null;
  originalValue: number | null;
  scaleFactor: number;
  onChange: (valueInCents: number | null) => void;
  disabled?: boolean;
  hasActiveOverride?: boolean;
  product: string;
};

const isValidInput = (val: string) => {
  if (val === '' || val.toUpperCase() === 'TBC') return true;
  return !isNaN(parseFloat(val)) && isFinite(Number(val)) && Number(val) >= 0;
};

const EditablePriceField = ({
  fieldKey,
  value,
  originalValue,
  scaleFactor,
  onChange,
  disabled = false,
  hasActiveOverride = false,
  product,
}: EditablePriceFieldProps) => {
  const {
    pendingResets,
    markForReset,
    deleteOverride,
    resetPreviews,
    isLoadingResetPreviews,
  } = useStaffPriceEditContext();

  const isPendingReset = pendingResets.has(product);
  const previewValue = isPendingReset ? (resetPreviews[product] ?? null) : null;

  const effectiveValue = isPendingReset ? previewValue : value;

  const displayValue = useMemo(() => {
    if (effectiveValue === null) return 'TBC';
    return (effectiveValue / scaleFactor).toFixed(2);
  }, [effectiveValue, scaleFactor]);

  // Track the last value this component committed to the parent so the
  // sync effect only fires for genuinely external changes.
  const lastCommittedRef = useRef(effectiveValue);

  const [localValue, setLocalValue] = useState(displayValue);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (effectiveValue !== lastCommittedRef.current) {
      setLocalValue(displayValue);
    }
    lastCommittedRef.current = effectiveValue;
  }, [effectiveValue, displayValue]);

  const isModified = useMemo(() => {
    return hasActiveOverride || value !== originalValue;
  }, [hasActiveOverride, value, originalValue]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const raw = e.target.value;
      setLocalValue(raw);

      if (!isValidInput(raw)) {
        setError(true);
        return;
      }

      setError(false);

      if (raw === '' || raw.toUpperCase() === 'TBC') {
        lastCommittedRef.current = null;
        onChange(null);
      } else {
        const parsed = parseFloat(raw);
        if (!isNaN(parsed) && parsed >= 0) {
          const cents = Math.round(parsed * scaleFactor);
          lastCommittedRef.current = cents;
          onChange(cents);
        }
      }
    },
    [onChange, scaleFactor]
  );

  const handleBlur = useCallback(() => {
    if (error) {
      setLocalValue(displayValue);
      setError(false);
      return;
    }
    if (localValue.toUpperCase() === 'TBC' || localValue === '') {
      setLocalValue('TBC');
    } else if (effectiveValue !== null) {
      setLocalValue((effectiveValue / scaleFactor).toFixed(2));
    }
  }, [effectiveValue, scaleFactor, error, displayValue, localValue]);

  const handleReset = useCallback(
    (ev: React.MouseEvent) => {
      ev.stopPropagation();
      if (hasActiveOverride) {
        markForReset(product);
      } else if (isModified) {
        deleteOverride(product);
      }
    },
    [hasActiveOverride, isModified, markForReset, deleteOverride, product]
  );

  const showResetIcon = !isPendingReset && (hasActiveOverride || isModified);
  const isPreviewLoading = isPendingReset && isLoadingResetPreviews;

  return (
    <div className='editable-price-field-wrapper'>
      {showResetIcon && (
        <OverlayTrigger
          placement='top'
          delay={{ show: 300, hide: 300 }}
          overlay={<Tooltip id={`tooltip-${fieldKey}`}>Default revert</Tooltip>}
        >
          <div className='default-revert' onClick={handleReset}>
            <Icon icon='undo-round' hasIconClass={false} />
          </div>
        </OverlayTrigger>
      )}
      {isPreviewLoading ? (
        <Spinner animation='border' className='size-12 grey-3' />
      ) : (
        <Form.Control
          size='sm'
          value={localValue}
          onChange={handleChange}
          onBlur={handleBlur}
          disabled={disabled}
          aria-label={`Price for ${product}`}
          className={classNames('editable-price-field', {
            isModified: isModified && !isPendingReset,
            'border-warning': isModified && !isPendingReset,
            'has-error': error,
          })}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
        />
      )}
      {error && (
        <div className='editable-price-field-error'>
          {text('numberOrTbcOnly')}
        </div>
      )}
    </div>
  );
};

export default EditablePriceField;
